import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faArchive, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useTaskListTemplates from '@hooks/useTaskListTemplates'
import { generateUrls } from '@hooks/useTaskListTemplate'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'

import TaskListTemplateListItem from '../TaskListTemplateListItem'

import type { PageContextPayload } from '../../index'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden === 'true'

  const taskListTemplatesPayload = useTaskListTemplates({
    filters: {
      ...pageFilters,
      hidden: isHidden,
      template_checklist: true,
    },
    performHttpRequests: true,
    requestOptions: {},
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListTemplates,
    filteredTaskListTemplatesCount,
    hasFilteredTaskListTemplates,
    loading,
  } = taskListTemplatesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrEditTaskListTemplateModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { taskListTemplatesIndexUrl, taskListHiddenTemplatesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Checklist Templates | Template Manager</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrEditTaskListTemplateModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden ? 'hidden' : 'current'}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: taskListTemplatesIndexUrl,
            icon: faCheckCircle,
            key: 'current',
            title: 'Current',
          },
          {
            href: taskListHiddenTemplatesIndexUrl,
            icon: faArchive,
            key: 'hidden',
            title: 'Archived',
          },
        ]}
        title="Checklist Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTaskListTemplates && <ListBlankState />}

            {!loading && hasFilteredTaskListTemplates && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Templates">
                <Box flexDirection="column">
                  {filteredTaskListTemplates.map(taskListTemplate => (
                    <TaskListTemplateListItem
                      key={taskListTemplate.id}
                      taskListTemplate={taskListTemplate}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTaskListTemplatesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
